import { Heading, IconButton } from 'copilot-design-system';
import React, { FC, useState } from 'react';

import * as Constants from 'src/constants';
import { Client, Company, PERM_MOD_CRM, PLAN_PAGE } from 'src/constants';
import { CRM_OPERATIONS } from 'src/constants/permissionConsts';
import { PortalConfigContext } from 'src/context';
import { ClientCompanyManager } from 'src/features/clients/ClientCompanyManager';
import { useCompanyClientAssignment } from 'src/features/crm/hooks/useCompanyClientAssignment';
import { useDataLossWarning } from 'src/features/crm/hooks/useDataLossWarning';
import { useClientCompanies } from 'src/hooks/useClientCompanies';
import { useAppSelector } from 'src/hooks/useStore';
// TODO: move to new components folder
import { CustomPropertiesContainer as ClientCustomFields } from 'src/legacy/components/ClientDetailsPage/PropertiesSection';
import { useSettingsPageRedirection } from 'src/legacy/components/Settings/useSettingsPageRedirection';
import { CompanyDetailsClients } from 'src/routes/companies/details/CompanyDetailsClients';
import { useCreateCompanyMutation } from 'src/services/api/companiesApi';
import { RootState } from 'src/store';
import {
  selectAllCompaniesMap,
  selectClientsMap,
} from 'src/store/clients/types';
import * as UserUtils from 'src/utils/UserUtils';
import { hasOperation } from 'src/utils/UserUtils';

export type ContextPropertiesProps = {
  entityType: 'client' | 'company';
  entityId: string;
  clients?: Client[];
};

type PropertiesProps = {
  client?: Client;
  company?: Company;
};

const Properties = ({ client, company }: PropertiesProps) => {
  const { permissions } = useAppSelector((state: any) => ({
    permissions: state.user.data?.permissions,
  }));
  const [propertiesMenuAnchor, setPropertiesMenuAnchor] =
    useState<HTMLElement | null>(null);

  const canUpdateCustomFields = hasOperation(
    Constants.PERM_MOD_CUSTOM_FIELDS,
    Constants.CUSTOM_FIELD_OPERATIONS.CHANGE_TEMPLATE,
    permissions,
  );

  return (
    <>
      <header className="[&_svg]:cursor-pointer [&_button]:cursor-pointer w-full pr-3 flex flex-row justify-between pb-3">
        <Heading size="base">Custom fields</Heading>
        {canUpdateCustomFields && (
          <IconButton
            className="p-1 hover:bg-secondary-hover"
            label="Create"
            onClick={(e) => {
              setPropertiesMenuAnchor(e.currentTarget);
            }}
            icon="Plus"
            variant="minimal"
          />
        )}
      </header>
      <ClientCustomFields
        currentClient={client}
        currentCompany={company}
        setCustomFieldMenuAnchor={setPropertiesMenuAnchor}
        customFieldMenuAnchor={propertiesMenuAnchor}
      />
    </>
  );
};

export const ContextProperties: FC<ContextPropertiesProps> = ({
  entityType,
  entityId,
  clients: companyClients,
}) => {
  const companies = useAppSelector(selectAllCompaniesMap);
  const clients = useAppSelector(selectClientsMap);
  const client = clients.get(entityId);
  const company = companies.get(entityId);
  const portalConfig = React.useContext(PortalConfigContext);
  const companiesEnabled = !portalConfig.features.disableCompanies;
  const clientCompanies = useClientCompanies(client);
  const { removeClientWithWarning, DataLossWarningModal } =
    useDataLossWarning();

  const [createCompany] = useCreateCompanyMutation();
  const {
    assignClientToCompany,
    PlaceholderPromotionModal,
    PlaceholderDeletionModal,
  } = useCompanyClientAssignment();

  const permissions = useAppSelector(
    (state: RootState) => state.user.data?.permissions,
  );
  const operation = UserUtils.getOperation(
    PERM_MOD_CRM,
    CRM_OPERATIONS.ADD_MULTIPLE_COMPANIES_TO_CLIENT,
    permissions,
  );

  const { goToSettings: goToPlansSettings } = useSettingsPageRedirection(
    true,
    PLAN_PAGE.path,
  );
  const onUpgradePlan = () => goToPlansSettings();

  const handleAddCompany = async ({
    company: companyParam,
    client: clientParam,
    promotePlaceholders = false,
    deletePlaceholder = false,
  }: {
    company: Company | { name: string };
    client: Client;
    promotePlaceholders?: boolean;
    deletePlaceholder?: boolean;
  }) => {
    if ('id' in companyParam) {
      await assignClientToCompany({
        company: companyParam,
        client: clientParam,
        promotePlaceholders,
        deletePlaceholder,
      });
    } else {
      await createCompany({
        name: companyParam.name,
        clients: [{ id: clientParam.id }],
      });
    }
  };

  return (
    <>
      <div className="w-full h-full ">
        {entityType === 'client' && client && (
          <>
            {companiesEnabled && (
              <ClientCompanyManager
                maxCompaniesAllowed={operation?.planLimit || 1}
                onUpgradePlan={onUpgradePlan}
                client={client}
                companies={clientCompanies}
                onRemoveCompany={(args) => {
                  void removeClientWithWarning({
                    companyId: args.company.id,
                    clientId: args.client.id,
                    companyName: args.company.fields.name,
                    isLastCompany: clientCompanies.length <= 1,
                  });
                }}
                onAddCompany={handleAddCompany}
              />
            )}
            <div className="px-4">
              <Properties client={client} />
            </div>
          </>
        )}
        {entityType === 'company' && company && (
          <>
            <CompanyDetailsClients
              clients={companyClients ?? []}
              company={company}
            />
            <div className="px-4">
              <Properties company={company} />
            </div>
          </>
        )}
      </div>
      <DataLossWarningModal />
      <PlaceholderPromotionModal />
      <PlaceholderDeletionModal />
    </>
  );
};
